/**
 * DAVIDsTEA — hides the "Buy More, Save More" panel (blocks/dt-promo-collapsible.liquid) while a
 * subscription is the selected purchase option, and brings it back on "One time purchase".
 *
 * The two discounts don't combine: the volume tiers are a mix-and-match promotion on one-off
 * packs, so advertising "get 20% off" above a subscription that is already discounting the same
 * item reads as an offer the shopper can't actually take.
 *
 * Why this listens rather than looking:
 *
 * Skio's picker (assets/skio-plan-picker-component.js) renders into a shadow root — confirmed
 * live, and noted in blocks/skio-app-block.liquid — so no selector from this document can reach
 * the radio that is actually checked, and no :has() rule can be written against it. What the
 * component does expose to the light DOM is the hidden `input[name="selling_plan"]` it submits
 * with the form: its own updateForm() writes the selected plan's id there (empty string for
 * one-time) and dispatches a `change` on it, on every update it makes.
 *
 * That event is dispatched with `new Event('change')`, so it does not bubble — hence the capture
 * phase below, which still sees an event on any descendant. The component's own
 * `skio::update-selling-plan` does bubble and is composed, and is listened for as well: it
 * carries the same answer and arrives from any picker, including one this file never sees the
 * input of.
 *
 * The answer is written to <html> rather than to the panel, and the panel is hidden by a CSS rule
 * that reads it (see the block's own stylesheet). <html> survives the section morph that
 * assets/product-form.js performs on every variant change, so a panel that is re-rendered mid-
 * page comes back in the right state on its first paint, with nothing to re-bind.
 */

const STATE_KEY = 'dtSellingPlan';

/** @param {boolean} hasSellingPlan */
function apply(hasSellingPlan) {
  document.documentElement.dataset[STATE_KEY] = hasSellingPlan ? 'subscription' : 'onetime';
}

document.addEventListener(
  'change',
  (event) => {
    const target = event.target;
    if (target instanceof HTMLInputElement && target.name === 'selling_plan') {
      apply(target.value !== '');
    }
  },
  true
);

document.addEventListener('skio::update-selling-plan', (event) => {
  apply(Boolean(/** @type {CustomEvent} */ (event).detail?.sellingPlan));
});

/* Opening state. The panel is rendered hidden by the block whenever the product is one Skio will
   draw a picker for, because every product template in this theme configures the picker with
   start_onetime: false — a subscription is selected before the shopper touches anything, and a
   panel that showed first and then vanished would be a flash of the wrong answer.

   So nothing is applied here while a picker is present: its first updateForm() runs as soon as
   the component's module executes and settles the question. Applied immediately only when there
   is no picker on the page at all, where one-time is the only thing the shopper can pick.

   The timeout is the failure path — a picker that never initialises (its script blocked, or a
   variant with no plan allocations that renders nothing) would otherwise leave the panel hidden
   for good. It fails open: showing the promotion to a shopper buying one-off is the harmless
   direction of a wrong guess. */
if (document.querySelector('input[name="selling_plan"]') == null) {
  apply(false);
} else {
  setTimeout(() => {
    if (document.documentElement.dataset[STATE_KEY] == null) apply(false);
  }, 1500);
}
